import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../_common/application/ts/Glob';
import { ParameterField } from '../../../../_common/platform/tsx/ParameterField';
import BooleanField from '../../../../_common/platform/tsx/BooleanField';

import AttributeDesigner, {
    AttributeDesignerMode,
    IToggleStates
} from '../../../../_common/platform/tsx/attributeDesigner/AttributeDesigner';

export { IToggleStates } from '../../../../_common/platform/tsx/attributeDesigner/AttributeDesigner';

import { ATTRIBUTE_CONTROL_TYPES } from '../../../platform/ts/ConfigurationConstants';
import { CLICK_TO_FILTER_NOT_USED_VALUE } from '../../../platform/tsx/filtering/Constants';
import { ITreeItem } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';
import { IAttribute, IAttributeName } from '../../../../_common/platform/tsx/filtering/Interfaces';
import GroupingParameterField from '../GroupingParameterField';

export default function ClickToFilterAttributeSelect(props: {
    attributeName: IAttributeName;
    dataSourceID: number;
    entitiesTree: ITreeItem[];
    disableByOption: boolean;
    allowNullClickToFilterAttribute?: boolean;
    defaultMode?: AttributeDesignerMode;
    tabID: number;
    glob: IGlob;

    value?: IAttribute;
    title?: string;
    entityCollapsedDict?: IToggleStates;

    onChange(value: IAttribute): void;
    onEntityCollapsedDictChange?(value: IToggleStates): void;
}) {
    if (props.attributeName == null) {
        return null;
    }

    const getValue = () => {
        if (typeof props.value === 'string') {
            return {
                attribute: props.value
            };
        } else {
            return props.value;
        }
    };

    const getValueCopy = (): IAttribute => {
        const value = getValue();

        return value != null ? $.extend(true, {}, value) : value;
    };

    const processValueChange = (value: IAttribute) => {
        if (typeof props.onChange === 'function') {
            props.onChange(value);
        }
    };

    const handleOnEnableChange = (value: boolean) => {
        let result: IAttribute;

        if (value) {
            result = getValueCopy();

            if (result != null && result.attribute === CLICK_TO_FILTER_NOT_USED_VALUE) {
                result = null;
            }
        } else {
            result = {
                attribute: CLICK_TO_FILTER_NOT_USED_VALUE
            };
        }

        processValueChange(result);
    };

    const handleOnAttributeChange = (values: string[]) => {
        let result: IAttribute;

        const attribute = values[0];

        if (attribute === props.attributeName.attribute) {
            result = null;
        } else {
            result = getValueCopy();

            if (result == null) {
                result = {
                    attribute: attribute
                };
            } else {
                result.attribute = attribute;
            }

            if (result.valueType === 'variable') {
                delete result.valueType;
            }
        }

        processValueChange(result);
    };

    const handleOnVariableNameChange = (value: string) => {
        if (value == null) {
            return;
        }

        let result: IAttribute;

        if (value === props.attributeName.attribute) {
            result = null;
        } else {
            result = getValueCopy();

            if (result == null) {
                result = {
                    attribute: value
                };
            } else {
                result.attribute = value;
            }

            result.valueType = 'variable';
            result.controlType = ATTRIBUTE_CONTROL_TYPES.DROPDOWN;

            // When Click to Filter Attribute is Variable, the Data Query is the main Attribute.
            if (result.options != null && result.options.dataQuery != null) {
                delete result.options.dataQuery;
            }
        }

        processValueChange(result);
    };

    const handleOnRemove = () => {
        processValueChange(null);
    };

    let title = props.title;

    if (title == null) {
        title = 'Click-to-filter';
    }

    const value = getValue();

    let attribute = value != null ? value.attribute : props.attributeName.attribute;

    const allowNullClickToFilterAttribute = props.allowNullClickToFilterAttribute !== false;
    const attributeIsSet = attribute !== props.attributeName.attribute;

    let enable = attribute !== CLICK_TO_FILTER_NOT_USED_VALUE;
    let componentEnable: React.JSX.Element;
    let componentAttribute: React.JSX.Element;

    if (allowNullClickToFilterAttribute) {
        if (props.attributeName.attribute != null) {
            componentEnable = (
                <ParameterField noTitle={true} clearable={false}>
                    <BooleanField title={'Enable'} value={enable} allowNull={false} onChange={handleOnEnableChange} />
                </ParameterField>
            );
        }
    } else if (!attributeIsSet || !enable) {
        attribute = null;
        enable = true;
    }

    if (enable) {
        const entitiesTree: ITreeItem[] = [];

        if (Array.isArray(props.entitiesTree)) {
            props.entitiesTree.forEach((entity) => {
                if (entity.original == null || !entity.original.IsFunc) {
                    entitiesTree.push(entity);
                }
            });
        }

        let currentMode: AttributeDesignerMode;

        if (value != null && value.valueType === 'variable') {
            currentMode = 'variable';
        } else if (value == null && (props.defaultMode === 'attribute' || props.defaultMode === 'variable')) {
            currentMode = props.defaultMode;
        } else {
            currentMode = 'attribute';
        }

        let title: string;

        if (currentMode === 'variable') {
            title = 'Add Value to Variable';
        } else {
            title = 'Add Value to filter as Attribute';
        }

        componentAttribute = (
            <ParameterField title={title} clearable={false}>
                <AttributeDesigner
                    value={attribute}
                    tree={entitiesTree}
                    dataSourceID={props.dataSourceID}
                    modes={['attribute', 'variable']}
                    currentMode={currentMode}
                    collapsedDict={props.entityCollapsedDict}
                    showRemoveButton={attributeIsSet}
                    tabID={props.tabID}
                    glob={props.glob}
                    onChange={handleOnAttributeChange}
                    onVariableNameChange={handleOnVariableNameChange}
                    onRemove={handleOnRemove}
                    onCollapsedDictChange={props.onEntityCollapsedDictChange}
                />
            </ParameterField>
        );
    }

    let disableByOptionWarning: React.JSX.Element;

    if (enable && props.disableByOption) {
        disableByOptionWarning = (
            <div
                style={{
                    color: 'red'
                }}
            >
                <span>{'Warning: Click-to-filter is turned off for Workspace/Widget'}</span>
            </div>
        );
    }

    return (
        <GroupingParameterField title={title}>
            {componentEnable}
            {componentAttribute}
            {disableByOptionWarning}
        </GroupingParameterField>
    );
}
